import {
  Checkbox as ArkCheckbox,
  type CheckboxRootProps as ArkCheckboxProps
} from '@ark-ui/solid';
import { JSXElement, Show, splitProps } from 'solid-js';
import { tv, type VariantProps } from 'tailwind-variants';
import './checkbox.scss';
import { twMerge } from 'tailwind-merge';

export interface CheckboxProps extends ArkCheckboxProps, CheckboxVariantProps {}

export const Checkbox = (props: CheckboxProps) => {
  const [variantProps, avatarProps] = splitProps(props, [
    'size',
    'class',
    'variant'
  ]);
  const [localProps, rootProps] = splitProps(avatarProps, ['children']);
  // @ts-expect-error https://github.com/nextui-org/tailwind-variants/issues/145
  const { root, control, label, indicator } = checkbox(variantProps);

  // const getChildren = children(() => localProps.children);

  return (
    <ArkCheckbox.Root
      class={root()}
      {...rootProps}
    >
      <ArkCheckbox.Control class={twMerge(control())}>
        <Show
          when={rootProps.checked === 'indeterminate'}
          fallback={
            <ArkCheckbox.Indicator class={indicator()}>
              <CheckIcon />
            </ArkCheckbox.Indicator>
          }
        >
          <ArkCheckbox.Indicator
            class={indicator()}
            indeterminate={true}
          >
            <MinusIcon />
          </ArkCheckbox.Indicator>
        </Show>
      </ArkCheckbox.Control>
      <Show when={localProps.children}>
        <ArkCheckbox.Label class={label()}>
          {localProps.children as JSXElement}
        </ArkCheckbox.Label>
      </Show>
      <ArkCheckbox.HiddenInput />
    </ArkCheckbox.Root>
  );
};

type CheckboxVariantProps = VariantProps<typeof checkbox>;

const checkbox = tv(
  {
    base: 'checkbox',
    defaultVariants: { size: 'md', variant: 'primary' },
    slots: {
      root: 'checkbox__root',
      label: 'checkbox__label',
      control: 'checkbox__control',
      indicator: 'checkbox__indicator'
    },
    variants: {
      variant: {
        primary: {
          root: 'checkbox__root--variant_primary',
          label: 'checkbox__label--variant_primary',
          control: 'checkbox__control--variant_primary',
          indicator: 'checkbox__indicator--variant_primary'
        },
        success: {
          root: 'checkbox__root--variant_success',
          label: 'checkbox__label--variant_success',
          control: 'checkbox__control--variant_success',
          indicator: 'checkbox__indicator--variant_success'
        }
      },
      size: {
        sm: {
          root: 'checkbox__root--size_sm',
          label: 'checkbox__label--size_sm',
          control: 'checkbox__control--size_sm',
          indicator: 'checkbox__indicator--size_sm'
        },
        md: {
          root: 'checkbox__root--size_md',
          label: 'checkbox__label--size_md',
          control: 'checkbox__control--size_md',
          indicator: 'checkbox__indicator--size_md'
        },
        lg: {
          root: 'checkbox__root--size_lg',
          label: 'checkbox__label--size_lg',
          control: 'checkbox__control--size_lg',
          indicator: 'checkbox__indicator--size_lg'
        }
      }
    }
  },
  { twMerge: false }
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Check</title>
    <path
      d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const MinusIcon = () => (
  <svg
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Minus</title>
    <path
      d="M2.91675 7H11.0834"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);
export default Checkbox;
